/**
 * Merge-tag "chips" — the display layer for personalization tokens.
 *
 * The block CONTENT always stores raw tokens (`{name}`, `{cf:<id>}`) so the
 * backend (replaceTemplateVars) and email renderer keep working unchanged.
 * Chips exist only for DISPLAY: in the live editor (contentEditable) and in the
 * non-edit canvas preview we show each known token as a small pill bearing its
 * human label, so the user sees "שם פרטי" instead of a meaningless `{cf:...}`.
 *
 * Two directions:
 *   - decorate  : tokens → chips   (when seeding an editor / rendering preview)
 *   - serialize : chips  → tokens  (when reading an editor's content back out)
 */
import { esc, looksLikeHtml, sanitizeEmailHtml } from './richText';

export interface MergeField {
  token: string;
  label: string;
}

export const CHIP_CLASS = 'amt-merge';

// Inline-styled so it renders identically inside a contentEditable AND inside a
// dangerouslySetInnerHTML preview, with no dependency on a stylesheet.
const CHIP_STYLE =
  'display:inline-block;background:#d1fae5;color:#065f46;border-radius:6px;' +
  'padding:0 6px;margin:0 1px;font-size:0.9em;font-weight:600;white-space:nowrap;' +
  'line-height:1.5;';

function escAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The HTML for a single chip. `contenteditable=false` makes it an atomic unit. */
export function chipSpanHtml(token: string, label: string): string {
  return (
    `<span class="${CHIP_CLASS}" data-token="${escAttr(token)}" contenteditable="false" ` +
    `style="${CHIP_STYLE}" dir="rtl">${esc(label || token)}</span>`
  );
}

/** Same chip as a real DOM node (for Range-based insertion). */
export function createChipElement(token: string, label: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = CHIP_CLASS;
  span.setAttribute('data-token', token);
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('style', CHIP_STYLE);
  span.setAttribute('dir', 'rtl');
  span.textContent = label || token;
  return span;
}

/**
 * Insert a chip at the caret using the Range API (NOT execCommand). execCommand
 * 'insertHTML' is unreliable for atomic inline elements — WebKit in particular
 * leaves a trailing <br>/<div>, which visually drops the caret to a new line.
 * Range insertion keeps the chip on the same line and parks the caret right
 * after it so the user can keep typing inline.
 *
 * `range` is the caret to insert at (e.g. the one saved before the picker stole
 * focus). Falls back to the end of `editorEl` when missing/stale.
 */
export function insertChipAtRange(
  editorEl: HTMLElement,
  range: Range | null,
  token: string,
  label: string,
): void {
  let r = range;
  if (!r || !editorEl.contains(r.commonAncestorContainer)) {
    r = document.createRange();
    r.selectNodeContents(editorEl);
    r.collapse(false); // end
  }
  r.deleteContents();
  const chip = createChipElement(token, label);
  r.insertNode(chip);
  // Park the caret immediately AFTER the chip, inline — no <br>, no new line.
  const after = document.createRange();
  after.setStartAfter(chip);
  after.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(after);
}

/**
 * Backspace/Delete safety net: if the caret sits right next to a chip, remove
 * the whole chip in one keypress (some browsers don't treat the atom as a
 * single deletable unit). Returns true if it handled the deletion.
 */
export function deleteAdjacentChip(editorEl: HTMLElement, direction: 'back' | 'forward'): boolean {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return false;
  const range = sel.getRangeAt(0);
  if (!range.collapsed) return false;
  const { startContainer: node, startOffset: offset } = range;

  const isChip = (n: Node | null): n is HTMLElement =>
    !!n && n.nodeType === Node.ELEMENT_NODE && (n as HTMLElement).classList.contains(CHIP_CLASS);

  let target: Node | null = null;
  if (node.nodeType === Node.TEXT_NODE) {
    // Only when the caret is at the very edge of the text node.
    if (direction === 'back' && offset === 0) target = node.previousSibling;
    else if (direction === 'forward' && offset === (node.nodeValue || '').length) target = node.nextSibling;
  } else {
    target = direction === 'back' ? node.childNodes[offset - 1] : node.childNodes[offset];
  }

  if (isChip(target)) {
    const caret = document.createRange();
    if (direction === 'back') caret.setStartBefore(target);
    else caret.setStartAfter(target);
    caret.collapse(true);
    (target as HTMLElement).remove();
    sel.removeAllRanges();
    sel.addRange(caret);
    return true;
  }
  return false;
}

/** A regex matching any of the known tokens (longest first), or null if none. */
function buildTokenRegex(fields: MergeField[]): RegExp | null {
  if (!fields || fields.length === 0) return null;
  const pattern = fields
    .map((f) => f.token)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(escRegExp)
    .join('|');
  if (!pattern) return null;
  return new RegExp(pattern, 'g');
}

export function contentHasToken(content: string, fields: MergeField[]): boolean {
  const re = buildTokenRegex(fields);
  return !!re && re.test(content || '');
}

// ─── Live editor (DOM) ────────────────────────────────────────────────────

/**
 * Walk a contentEditable element and wrap every known token (inside text
 * nodes) in a chip span. Idempotent-ish: text already inside a chip is skipped.
 * Mutates `root` in place; call once after seeding the editor.
 */
export function decorateEditorDom(root: HTMLElement, fields: MergeField[]): void {
  const re = buildTokenRegex(fields);
  if (!re) return;
  const labelByToken = new Map(fields.map((f) => [f.token, f.label]));

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const targets: Text[] = [];
  let n: Node | null;
  // eslint-disable-next-line no-cond-assign
  while ((n = walker.nextNode())) {
    const parent = (n as Text).parentElement;
    if (parent && parent.closest('.' + CHIP_CLASS)) continue; // already chipped
    re.lastIndex = 0;
    if (re.test((n as Text).nodeValue || '')) targets.push(n as Text);
  }

  targets.forEach((tn) => {
    const text = tn.nodeValue || '';
    const frag = document.createDocumentFragment();
    let last = 0;
    let m: RegExpExecArray | null;
    re.lastIndex = 0;
    // eslint-disable-next-line no-cond-assign
    while ((m = re.exec(text))) {
      if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
      const tmp = document.createElement('div');
      tmp.innerHTML = chipSpanHtml(m[0], labelByToken.get(m[0]) || m[0]);
      frag.appendChild(tmp.firstChild as Node);
      last = m.index + m[0].length;
    }
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    tn.parentNode?.replaceChild(frag, tn);
  });
}

/** Recursively read an element's text, turning chips back into their token. */
function nodeToTokenText(node: Node): string {
  let out = '';
  node.childNodes.forEach((ch) => {
    if (ch.nodeType === Node.TEXT_NODE) {
      out += ch.nodeValue || '';
    } else if (ch.nodeName === 'BR') {
      out += '\n';
    } else if ((ch as HTMLElement).classList?.contains(CHIP_CLASS)) {
      out += (ch as HTMLElement).getAttribute('data-token') || '';
    } else {
      const isBlock = ch.nodeName === 'DIV' || ch.nodeName === 'P';
      if (isBlock && out && !out.endsWith('\n')) out += '\n';
      out += nodeToTokenText(ch);
    }
  });
  return out;
}

/**
 * Read an editor's content back as storable tokens. `rich` blocks return HTML
 * (chips → token text, other markup preserved); plain blocks return text with
 * newlines (mirrors the old innerText path).
 */
export function serializeEditor(el: HTMLElement, rich: boolean): string {
  const hasChip = !!el.querySelector('.' + CHIP_CLASS);
  if (rich) {
    if (!hasChip) return stripInlineImages(el.innerHTML);
    const clone = el.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('.' + CHIP_CLASS).forEach((c) => {
      c.replaceWith(document.createTextNode(c.getAttribute('data-token') || ''));
    });
    return stripInlineImages(clone.innerHTML);
  }
  if (!hasChip) return el.innerText || '';
  return nodeToTokenText(el);
}

// Safety net: images never belong inline in a text block (they bloat the doc
// past MongoDB's 16MB limit as base64). Paste/drop are already blocked, but
// strip on read too so nothing slips through any other vector.
function stripInlineImages(html: string): string {
  return html.indexOf('<img') === -1 ? html : html.replace(/<img\b[^>]*>/gi, '');
}

// ─── Non-edit preview (string) ────────────────────────────────────────────

/**
 * Turn stored content into display HTML with chips, for the non-edit canvas
 * preview. `isHtml` content is sanitized first; plain content is escaped (and
 * the caller keeps white-space:pre-wrap so newlines survive). Only tokens that
 * are known fields become chips — unknown ones stay as literal text.
 */
export function decorateDisplayHtml(content: string, fields: MergeField[]): string {
  const re = buildTokenRegex(fields);
  const isHtml = looksLikeHtml(content || '');
  let html = isHtml ? sanitizeEmailHtml(content || '') : esc(content || '');
  if (re) {
    const labelByToken = new Map(fields.map((f) => [f.token, f.label]));
    html = html.replace(re, (m) => chipSpanHtml(m, labelByToken.get(m) || m));
  }
  return html;
}
